import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Icon,
  ModalBody,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { IconType } from "react-icons";
import { FaCheck } from "react-icons/fa";
import { AnimatedChevronIcon } from "../../../../AnimatedChevronIcon";
import { AppModalFooter } from "../../../../modals/AppModalFooter";
import { BulkEditOptionType } from "./BulkEditModal";
import { bulkEditOperations } from "./common";

export function BulkEditSelectOptionStep({
  onSelectOperation,
  onCancel,
  transactionCount,
}: {
  onSelectOperation: (type: BulkEditOptionType) => void;
  onCancel: () => void;
  transactionCount: number;
}): React.ReactElement {
  return (
    <>
      <ModalBody>
        <Box mb={6}>
          <Text color="gray.600" mb={2}>
            {`Choose how you'd like to bulk update the selected transactions.`}
          </Text>

          <Tag
            size="md"
            variant="subtle"
            colorScheme="blue"
            borderRadius="full"
          >
            <TagLeftIcon boxSize="12px" as={FaCheck} />
            <TagLabel>
              {`${transactionCount.toLocaleString()} ${
                transactionCount === 1 ? "transaction" : "transactions"
              } selected`}
            </TagLabel>
          </Tag>
        </Box>

        <BulkEditOptionSelector onSelect={onSelectOperation} />
      </ModalBody>

      <AppModalFooter>
        <Button variant="outline" colorScheme="gray" onClick={onCancel}>
          Cancel
        </Button>
      </AppModalFooter>
    </>
  );
}

function BulkEditOptionSelector({
  onSelect,
}: {
  onSelect: (type: BulkEditOptionType) => void;
}): React.ReactElement {
  return (
    <VStack width="full" align="stretch" spacing={4}>
      {Object.entries(bulkEditOperations).map(
        ([type, { label, icon, description }]) => (
          <OptionButton
            key={type}
            label={label}
            icon={icon}
            description={description}
            onClick={() => onSelect(type as BulkEditOptionType)}
          />
        ),
      )}
    </VStack>
  );
}

export function OptionButton({
  label,
  icon,
  description,
  ...props
}: {
  label: string;
  icon: IconType;
  description: string;
} & ButtonProps): React.ReactElement {
  return (
    <Button
      role="group"
      variant="outline"
      colorScheme="gray"
      w="full"
      h="auto"
      justifyContent="flex-start"
      whiteSpace="normal"
      textAlign="left"
      shadow="sm"
      px={4}
      py={4}
      rightIcon={<AnimatedChevronIcon />}
      {...props}
    >
      <HStack w="full" align="flex-start" spacing={4}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxSize="2.25rem"
          borderRadius="full"
          bg="blue.50"
          flexShrink={0}
        >
          <Icon as={icon} boxSize={4} color="blue.500" />
        </Box>

        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="semibold">{label}</Text>
          <Text fontWeight="normal" color="gray.700" fontSize="sm">
            {description}
          </Text>
        </VStack>
      </HStack>
    </Button>
  );
}
